import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/shared/components/Dialog'
import { useRemoveMember } from './useRemoveMember'
import type { HouseholdMember } from './useHouseholdMembers'

interface Props {
  member: HouseholdMember
  currentUserId: string
  currentUserRole: 'Planner' | 'Member'
  isLastPlanner: boolean
}

export function MemberRow({ member, currentUserId, currentUserRole, isLastPlanner }: Props) {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const removeMember = useRemoveMember()

  const canRemove =
    currentUserRole === 'Planner' && !(member.userId === currentUserId && isLastPlanner)

  const handleConfirm = () => {
    removeMember.mutate(member.id, {
      onSuccess: () => setConfirmOpen(false),
    })
  }

  return (
    <tr className="border-b border-line last:border-0 hover:bg-accent-tint/40 transition-colors">
      <td className="px-4 py-3 text-ink font-medium">{member.displayName ?? member.email}</td>
      <td className="px-4 py-3 text-muted">{member.email}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          member.role === 'Planner'
            ? 'bg-accent-tint text-accent'
            : 'bg-surface border border-line text-muted'
        }`}>
          {member.role === 'Planner'
            ? t('household.role.planner')
            : t('household.role.member')}
        </span>
      </td>
      <td className="px-4 py-3 text-muted text-xs">
        {new Date(member.joinedAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 text-right">
        {canRemove && (
          <button
            onClick={() => setConfirmOpen(true)}
            className="text-xs text-muted hover:text-danger transition font-medium"
          >
            {t('household.members.removeButton')}
          </button>
        )}
      </td>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('household.members.removeConfirmTitle')}
      >
        <p className="text-sm text-muted">
          {t('household.members.removeConfirmMessage', {
            name: member.displayName ?? member.email,
          })}
        </p>

        {removeMember.error?.message.includes('400') && (
          <p role="alert" className="text-sm text-danger mt-2">{t('household.members.lastPlannerError')}</p>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleConfirm}
            disabled={removeMember.isPending}
            className="flex-1 rounded-lg bg-danger text-white font-semibold py-2.5 px-4 hover:bg-danger/90 focus:outline-none focus:ring-2 focus:ring-danger transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.delete')}
          </button>
          <button
            onClick={() => setConfirmOpen(false)}
            className="flex-1 rounded-lg border border-line bg-canvas text-ink font-medium py-2.5 px-4 hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent transition"
          >
            {t('common.cancel')}
          </button>
        </div>
      </Dialog>
    </tr>
  )
}
